import React from "react";
import { I } from "./icons";

// Seed data from CareCard Express spec §3.2 (BSO facilities) + placeholder content.
// Master spec · v2.7.2 · 30 June 2026
// v2.7.1: roster verified against sheriff.org — exactly four facilities; WBDC removed;
// all four official mail PO boxes seeded; seed is an upsert.
// v2.7.2: code audit — AWS credential wiring fixed (standard S3/Rekognition chain required,
// dedicated Rekognition IAM pair optional); workers/seed/scripts typecheck clean in CI; lint clean.

const SPEC_VERSION = "v2.7.2";
const SPEC_DRAFT = "Master spec";
const SPEC_DATE = "30 June 2026";

// Spec constants (§global) — v2.7.0
const D30_CONSTANTS = {
  RECONCILIATION_SCHEDULED_PAID_MISSED_MIN: 10,
  DISPUTE_EVIDENCE_WARNING_H: 48,
  ORPHANED_PAYMENT_AUTO_CANCEL_MIN: 15,
  CAPTURE_RETRY_MAX: 3,
  SCHEDULED_SEND_TIMEZONE: "America/New_York",
  STRIPE_TAX_ENABLED: false,   // v2.3.2 fork — flat-rate v1, single-FL nexus
  PROMO_FLOOR_CENTS: 50,       // Stripe minimum charge
};

const FACILITIES = [
  {
    slug: "main-jail",
    name: "Main Jail",
    address_line_1: "Recipient name / Arrest #",
    address_line_2: "PO Box 9356",
    city: "Ft. Lauderdale", state: "FL", zip: "33310",
    street: "555 SE 1st Ave, Ft. Lauderdale 33301",
    status: "OPERATIONAL",
    blurb: "Central intake facility · downtown Ft. Lauderdale",
  },
  {
    slug: "joseph-v-conte",
    name: "Joseph V. Conte Facility",
    address_line_1: "Recipient name / Arrest #",
    address_line_2: "PO Box 407016",
    city: "Ft. Lauderdale", state: "FL", zip: "33340-7016",
    street: "1351 NW 27th Ave, Pompano Beach 33069",
    status: "OPERATIONAL",
    blurb: "Medium-security · work programs",
  },
  {
    slug: "north-broward-bureau",
    name: "North Broward Bureau",
    address_line_1: "Recipient name / Arrest #",
    address_line_2: "PO Box 407037",
    city: "Ft. Lauderdale", state: "FL", zip: "33340",
    street: "1550 NW 30th Ave (Blount Rd), Pompano Beach 33069",
    status: "OPERATIONAL",
    blurb: "Women's detention · mental health unit",
  },
  {
    slug: "paul-rein-detention",
    name: "Paul Rein Detention Facility",
    address_line_1: "Recipient name / Arrest #",
    // PO Box 407003, the development display value per the current spec §5.1
    // (the 407003/407033 official-source conflict is unresolved — see §10.7,
    // Paul Rein stays fail-closed). The live list comes from the policy
    // engine, not from here — this static copy is only the no-backend fallback.
    address_line_2: "PO Box 407003",
    city: "Ft. Lauderdale", state: "FL", zip: "33340",
    street: "2421 NW 16 St, Pompano Beach 33069",
    status: "OPERATIONAL",
    blurb: "Minimum-security · reentry programs",
  },
  {
    // V50 (2026-11-04): St. Lucie County Jail. Personal mail goes to Smart
    // Communications' scanning center (SLCSO Inmate Mail & Property page,
    // fetched 2026-11-04); the person sees a scan on a tablet.
    slug: "st-lucie-county-jail",
    name: "St. Lucie County Jail",
    company_line: "Smart Communications / St. Lucie County",
    address_line_1: "Recipient name #Jail ID",
    address_line_2: "P.O. Box 9120",
    city: "Seminole", state: "FL", zip: "33775-9120",
    street: "900 N Rock Rd, Fort Pierce 34945",
    status: "OPERATIONAL",
    blurb: "St. Lucie County · mail is scanned to their tablet",
    delivery_mode: "scanned_to_tablet",
    id_rule: "st_lucie",
    housing_required: false,
    lookup_url: "https://www.stluciesheriff.com/215/Inmate-Lookup",
  },
];

// Realistic sample for the mock
const SAMPLE = {
  recipientName: "Marcus Whitfield",
  arrestNumber: "BCJ-2026-048217",
  facility: FACILITIES[0],
  message: `Hey Marcus,

I drove by the pier this morning — the sun was coming up over the water and I thought of those Sunday walks we used to take with Dad. Kayla made honor roll again. She's got your stubborn streak. Mom is doing okay; the new medication is helping her sleep.

We are keeping a seat at the table. Every Sunday. Keep your head up. 64 days.

Write when you can. We love you.

— Dana & the kids`,
  // Same field names as GuestOrderCreate's sender_* inputs and the `sender`
  // sub-document, so sample data and real data render through one code path.
  sender: {
    name: "Dana Whitfield",
    email: "dana.whitfield@gmail.com",
    line1: "4218 NW 71st Ave",
    line2: null,
    city: "Deerfield Beach",
    state: "FL",
    zip: "33441",
  },
};

// Unit economics per spec §33.1
const UNIT_ECON = {
  revenue_cents: 399,
  stripe_fee_cents: 42,
  postgrid_cents: 125,
  sightengine_cents: 1,
  postmark_cents: 0,   // amortized; negligible
  infra_cents: 0,      // amortized
  total_variable_cents: 168,
  gross_margin_cents: 231,
  margin_pct: 57.9,
  fixed_monthly_usd: 75,
  breakeven_orders_month: 33,
};

export { FACILITIES, SAMPLE, SPEC_VERSION, SPEC_DRAFT, SPEC_DATE, UNIT_ECON, D30_CONSTANTS };
